<template>
    <div class="w-full min-h-screen flex flex-col items-center justify-center text-center p-2 space-y-20"> 
        <div class="w-full flex flex-col space-y-5 max-w-md mx-auto">
            <h1 class="text-2xl">
                {{ $t('waitingPage.completed', $store.state.locale) }} {{ completionNumber }} / 4
            </h1>
            <!-- Steps -->
            <!-- Step 0 - Registration  -->
            <div class="flex items-center justify-start space-x-2 w-full">
                <div 
                    class="w-6 h-6 min-w-6 border rounded flex items-center justify-center"
                    :class="completionNumber >= 1 ? 'bg-light-green-500 border-light-green-500' : 'bg-white border-gray-500'">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 fill-current text-white"><path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"/></svg>
                </div>
                <span>{{ $t('waitingPage.steps.one', $store.state.locale) }}</span>
            </div>
            <!-- Step 1 - ZDHC Gateway supplier link  -->
            <div class="flex items-center justify-between space-x-10 w-full">
                <div class="flex items-center justify-start space-x-2">
                    <div
                        class="w-6 h-6 min-w-6 border rounded flex items-center justify-center"
                        :class="completionNumber >= 2 ? 'bg-light-green-500 border-light-green-500' : 'bg-white border-gray-500'">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 fill-current text-white"><path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"/></svg>
                    </div>
                    <span>{{ $t('waitingPage.steps.two', $store.state.locale) }}</span>
                </div>
                <button
                    :title="$t('waitingPage.needHelp', $store.state.locale)"
                    :aria-label="$t('waitingPage.needHelp', $store.state.locale)"
                    class="text-blue-500 hover:underline flex items-center justify-center p-1 text-sm"
                    @click.prevent="showHelpModal = true">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-6 min-w-6 fill-current text-light-green-500"><path d="M11.29,15.29a1.58,1.58,0,0,0-.12.15.76.76,0,0,0-.09.18.64.64,0,0,0-.06.18,1.36,1.36,0,0,0,0,.2.84.84,0,0,0,.08.38.9.9,0,0,0,.54.54.94.94,0,0,0,.76,0,.9.9,0,0,0,.54-.54A1,1,0,0,0,13,16a1,1,0,0,0-.29-.71A1,1,0,0,0,11.29,15.29ZM12,2A10,10,0,1,0,22,12,10,10,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8,8,0,0,1,12,20ZM12,7A3,3,0,0,0,9.4,8.5a1,1,0,1,0,1.73,1A1,1,0,0,1,12,9a1,1,0,0,1,0,2,1,1,0,0,0-1,1v1a1,1,0,0,0,2,0v-.18A3,3,0,0,0,12,7Z"/></svg>
                </button>
            </div>
            <!-- ZDHC GATEWAY LINK INSTRUCTIONS  -->
            <div v-if="completionNumber === 1" class="max-w-md mx-auto flex flex-col space-y-5 rounded shadow p-5">
                <h2 class="text-lg font-semibold text-left">
                    {{ $t('waitingPage.link.title', $store.state.locale) }}
                </h2>
                <ol class="text-left text-sm text-gray-600 list-decimal list-inside space-y-2">
                    <li>{{ $t('waitingPage.link.step_one', $store.state.locale) }}</li>
                    <li>{{ $t('waitingPage.link.step_two', $store.state.locale) }}</li>
                    <li>{{ $t('waitingPage.link.step_three', $store.state.locale) }}</li>
                </ol>
                <p class="text-left text-sm text-gray-400">
                    {{ $t('waitingPage.link.hint', $store.state.locale) }}
                </p>
                <button
                    class="text-light-green-500 hover:underline flex items-center justify-center p-1"
                    @click.prevent="checkSupplierLink"
                    v-html="setLinkCheckButtonContent"
                    :disabled="linkCheckStatus !== 'void'">
                </button>
                <button
                    class="text-blue-500 hover:underline flex items-center justify-center p-1 text-sm"
                    @click.prevent="showHelpModal = true">
                    {{ $t('waitingPage.needHelp', $store.state.locale) }}
                </button>
            </div>
            <!-- Step 2 - Subscription  -->
            <div class="flex items-center justify-start space-x-2 w-full">
                <div 
                    class="w-6 h-6 min-w-6 border rounded flex items-center justify-center"
                    :class="completionNumber >= 3 ? 'bg-light-green-500 border-light-green-500' : 'bg-white border-gray-500'">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 fill-current text-white"><path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"/></svg>
                </div>
                <span>{{ $t('waitingPage.steps.three', $store.state.locale) }}</span>
            </div>
            <div v-if="completionNumber === 2" class="w-full py-2 px-4 flex flex-col space-y-3">
                <h1 class="text-lg font-semibold">{{ $t('waitingPage.complete_subscription', $store.state.locale) }}</h1>
                <p class="text-sm text-gray-400 text-left">{{ $t('waitingPage.complete_subscription_link', $store.state.locale) }}</p>
                <div v-if="!stripeUrl" class="w-full flex items-center justify-center p-2">
                    <svg class="animate-spin w-7 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                </div>
                <a 
                    v-else
                    :href="stripeUrl" 
                    class="px-2 py-1 mx-auto block max-w-max text-light-green-500 border border-light-green-500 rounded hover:bg-light-green-500 hover:text-white duration-300">
                    {{ $t('waitingPage.complete_subscription_click', $store.state.locale) }}
                </a>
                
            </div>
            <!-- Step 4  -->
            <div class="flex items-center justify-start space-x-2 w-full">
                <div 
                    class="w-6 h-6 min-w-6 border rounded flex items-center justify-center"
                    :class="completionNumber >= 4 ? 'bg-light-green-500 border-light-green-500' : 'bg-white border-gray-500'">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="w-5 fill-current text-white"><path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"/></svg>
                </div>
                <span>{{ $t('waitingPage.steps.four', $store.state.locale) }}</span>
            </div>
            <div v-if="generatingTokenStatus === 'loading'" class="w-full py-2 px-4 flex flex-col items-center justify-center space-y-3">
                <svg class="animate-spin w-5 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                <span>{{ $t('waitingPage.link.finalizing', $store.state.locale) }}</span>
            </div>
            <div v-else-if="generatingTokenStatus === 'error'" class="w-full py-2 px-4 flex flex-col items-center justify-center space-y-3">
                <span class="text-red-500">Something went wrong in generating your access token. Please contact our customer support </span>
                <a 
                    class="w-full text-left p-2 group duration-300 flex items-center justify-center"
                    :href="`mailto:${supportEmail}`">
                    <h2 class="text-lg font-semibold group-hover:underline">
                        Click here for help
                    </h2>
                </a>
            </div>
            <div v-if="completionNumber >= 4" class="flex items-center justify-center lg:justify-end">
                <router-link 
                    :title="$t('auth.signup.signinButton', $store.state.locale)"
                    :to="`/${$store.state.locale}/dashboard`"
                    class="w-full lg:w-auto lg:max-w-max bg-blue-500 hover:bg-blue-300 flex items-center justify-center h-12 px-6 font-medium tracking-wide text-white transition duration-200 rounded shadow-md focus:shadow-outline focus:outline-none">
                    {{ $t('auth.signup.signinButton', $store.state.locale) }}
                </router-link>
            </div>
        </div>
        <div class="flex flex-col space-y-5">
            <button
                :title="$t('sidebar.logout', $store.state.locale)"
                class="text-lg flex items-center justify-center"
                @click.prevent="signout"
                :class="setLogoutClasses"
                v-html="setLogoutContent"
                :disabled="logoutStatus !== 'void'">
            </button>
            <button 
                :title="$t('actions.help', $store.state.locale)"
                class="text-blue-500 hover:underline"
                @click.prevent="showHelpModal = true">
                {{ $t('actions.help', $store.state.locale) }}
            </button>
        </div>
        <teleport to="body">
            <!-- Help modal -->
            <div v-if="showHelpModal" class="fixed inset-0 p-6 bg-gray-1000 bg-opacity-90 z-30 overscroll-y-contain overflow-y-auto text-blue-500">
                <div class="flex flex-col w-full h-full items-center justify-center">
                    <div class="bg-white dark:bg-gray-900 dark:text-gray-400 border dark:border-gray-700 rounded p-5 flex flex-col space-y-2 w-full md:w-2/3 lg:w-1/2 2xl:w-1/2 max-h-xl overflow-y-auto">
                        <div class="flex items-start justify-between">
                            <button 
                                title="Chiudi"
                                @click.prevent="closeHelpModal"
                                class="ml-auto bg-transparent min-w-min flex items-center justify-center transform hover:rotate-90 duration-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 64 64" class="w-4 fill-current text-gray-800 dark:text-gray-400">
                                        <path d="M34.5,32L62.2,4.2c0.7-0.7,0.7-1.8,0-2.5c-0.7-0.7-1.8-0.7-2.5,0L32,29.5L4.2,1.8c-0.7-0.7-1.8-0.7-2.5,0
                                            c-0.7,0.7-0.7,1.8,0,2.5L29.5,32L1.8,59.8c-0.7,0.7-0.7,1.8,0,2.5c0.3,0.3,0.8,0.5,1.2,0.5s0.9-0.2,1.2-0.5L32,34.5l27.7,27.8
                                            c0.3,0.3,0.8,0.5,1.2,0.5c0.4,0,0.9-0.2,1.2-0.5c0.7-0.7,0.7-1.8,0-2.5L34.5,32z"/>
                                    </svg>
                            </button>
                        </div>
                        <h1 class="text-xl flex items-center space-x-1 dark:text-gray-200">
                            <span>{{ $t('actions.help', $store.state.locale) }}</span> 
                        </h1>
                        <div class="flex flex-col space-y-5">
                            <button 
                                class="w-full text-left flex flex-col space-y-2 border rounded p-2 group hover:bg-gray-100 duration-300"
                                @click.prevent="noKeysAssistanceRequest"
                                :disabled="noKeysAssistanceStatus !== 'void'">
                                <h2 class="text-lg font-semibold group-hover:underline">
                                    {{ $t('waitingPage.help.no_keys_title', $store.state.locale) }}
                                </h2>
                                <p  
                                    class=" text-left" 
                                    :class="setNoKeysAssistanceButtonClasses"
                                    v-html="setNoKeysAssistanceButtonContent"></p>
                            </button>
                            <a 
                                class="w-full text-left flex flex-col space-y-2 border rounded p-2 group hover:bg-gray-100 duration-300"
                                :href="`mailto:${supportEmail}`"
                                :disabled="noKeysAssistanceStatus !== 'void'">
                                <h2 class="text-lg font-semibold group-hover:underline">
                                    Altro
                                </h2>
                                <p class="text-sm text-gray-400 text-left">
                                    Per qualunque necessità, clicca qui per contattare il nostro supporto clienti!    
                                </p>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="showHelpModal" class="fixed inset-0 bg-black bg-opacity-30 z-20 flex items-center justify-end"></div>
            <!--<div  class="fixed inset-0 p-6 bg-gray-1000 bg-opacity-90 z-30 overscroll-y-contain overflow-y-auto text-blue-500">
                <div class="flex flex-col w-full h-full items-center justify-center">
                    <div class="bg-white dark:bg-gray-900 dark:text-gray-400 border dark:border-gray-700 rounded p-5 flex flex-col space-y-2 w-full md:w-2/3 lg:w-1/2 2xl:w-1/2 max-h-xl overflow-y-auto">
                        <div class="flex items-start justify-between">
                            <button 
                                title="Chiudi"
                                @click.prevent="closeKeyHelpModal"
                                class="ml-auto bg-transparent min-w-min flex items-center justify-center transform hover:rotate-90 duration-300">
                                    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 64 64" class="w-4 fill-current text-gray-800 dark:text-gray-400">
                                        <path d="M34.5,32L62.2,4.2c0.7-0.7,0.7-1.8,0-2.5c-0.7-0.7-1.8-0.7-2.5,0L32,29.5L4.2,1.8c-0.7-0.7-1.8-0.7-2.5,0
                                            c-0.7,0.7-0.7,1.8,0,2.5L29.5,32L1.8,59.8c-0.7,0.7-0.7,1.8,0,2.5c0.3,0.3,0.8,0.5,1.2,0.5s0.9-0.2,1.2-0.5L32,34.5l27.7,27.8
                                            c0.3,0.3,0.8,0.5,1.2,0.5c0.4,0,0.9-0.2,1.2-0.5c0.7-0.7,0.7-1.8,0-2.5L34.5,32z"/>
                                    </svg>
                            </button>
                        </div>
                        <h1 class="text-xl flex flex-col text-left space-y-1 dark:text-gray-200">
                            <span>{{ $t('waitingPage.keyHelp.title', $store.state.locale) }}</span> 
                            <br>
                            <span class="text-sm">{{ $t('waitingPage.keyHelp.body', $store.state.locale) }}</span> 
                        </h1>
                        <div class="flex flex-col space-y-5">
                            <button 
                                class="w-full text-left flex flex-col space-y-2 border rounded p-2 group hover:bg-gray-100 duration-300"
                                @click.prevent="noKeysAssistanceRequest"
                                :disabled="noKeysAssistanceStatus !== 'void'">
                                <h2 class="text-lg font-semibold group-hover:underline">
                                    {{ $t('waitingPage.help.no_keys_title', $store.state.locale) }}
                                </h2>
                                <p  
                                    class=" text-left" 
                                    :class="setNoKeysAssistanceButtonClasses"
                                    v-html="setNoKeysAssistanceButtonContent"></p>
                            </button>
                            <a 
                                class="w-full text-left flex flex-col space-y-2 border rounded p-2 group hover:bg-gray-100 duration-300"
                                :href="`mailto:${supportEmail}`"
                                :disabled="noKeysAssistanceStatus !== 'void'">
                                <h2 class="text-lg font-semibold group-hover:underline">
                                    Altro
                                </h2>
                                <p class="text-sm text-gray-400 text-left">
                                    Per qualunque necessità, clicca qui per contattare il nostro supporto clienti!    
                                </p>
                            </a>
                        </div>
                    </div>
                </div>
            </div>-->
        </teleport>
    </div>
</template>
<script>
import {useStore} from 'vuex';
import {computed, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRouter, useRoute} from 'vue-router';
import AuthService from '../../services/AuthService';
import ProfileService from '../../services/ProfileService';
import StripeService from '../../services/StripeService';
import ZdhcService, { LINK_STATUS } from '../../services/Zdhc/ZdhcService';
import SnackbarComposables from '../../composables/SnackbarComposables';
import { exceptions_email } from '../../config/exceptions';
//import moment from 'moment';
export default
{
    setup()
    {
        /**
         *
         * Data
         *
         */
        const store = useStore();
        const router = useRouter();
        const route = useRoute();
        const showHelpModal = ref(false);
        const generatingTokenStatus = ref('void');
        const { t, locale } = useI18n();
        const completionNumber = ref(0);
        const logoutStatus = ref('void');
        const linkCheckStatus = ref('void');
        const supportEmail = process.env.VUE_APP_YMPACT_SUPPORT_EMAIL;
        const noKeysAssistanceStatus = ref('void');
        const { logout, checkAuthuser } = AuthService();
        const { addSnackbar } = SnackbarComposables();
        const { sendZDHCKeysRequestEmail, updateSuscriptionExpiration } = ProfileService();
        const { linkStatus, createSubscription } = ZdhcService();
        const { expiresSpecificCheckoutSession, verifySubscription, createCheckout } = StripeService();
        const stripeSubscriptions = ref([]);
        // const selectedPriceId = ref(process.env.VUE_APP_DISCOUNT_PRICE);
        const selectedPriceId = ref(process.env.VUE_APP_ANNUAL_PRICE);
        const stripeUrl = ref(null);

        /* 
         *
         * Computed
         *
         */
        const setLogoutContent = computed( () => 
        {
            switch(logoutStatus.value)
            {
                case 'loading':
                    return `<svg class="animate-spin h-5 w-5 fill-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`;
                case 'success':
                    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5 fill-current text-white"><path d="M18.71,7.21a1,1,0,0,0-1.42,0L9.84,14.67,6.71,11.53A1,1,0,1,0,5.29,13l3.84,3.84a1,1,0,0,0,1.42,0l8.16-8.16A1,1,0,0,0,18.71,7.21Z"/></svg>';
                case 'error':
                    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-5 w-5 fill-current text-white"><path d="M13.41,12l4.3-4.29a1,1,0,1,0-1.42-1.42L12,10.59,7.71,6.29A1,1,0,0,0,6.29,7.71L10.59,12l-4.3,4.29a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l4.29,4.3a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z"/></svg>`;
                case 'void': default:
                    return t('sidebar.logout', locale.value);
            }
        });
        const setLogoutClasses = computed( () =>
        {
            switch(logoutStatus.value)
            {
                case 'void': case 'loading': default:
                    return 'text-light-green-500 hover:text-light-green-300';
                case 'error':
                    return `text-red-500 hover:text-red-700`;
                case 'success':
                    return `text-green-500 hover:text-green-700`;
            }
        });
        const setLinkCheckButtonContent = computed( () =>
        {
            switch(linkCheckStatus.value)
            {
                case 'loading':
                    return `<svg class="animate-spin h-5 w-5 fill-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`;
                case 'void': default:
                    return t('waitingPage.link.check_button', store.state.locale);
            }
        });
        const setNoKeysAssistanceButtonContent = computed( () =>
        {
            switch(noKeysAssistanceStatus.value)
            {
                case 'loading':
                    return `<svg class="animate-spin h-9 w-9 fill-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>`;
                case 'success':
                    return t('waitingPage.help.taking_charge_message', store.state.locale);
                case 'void': default:
                    return t('waitingPage.help.no_keys_message', store.state.locale);
            }
        });
        const setNoKeysAssistanceButtonClasses = computed( () =>
        {
            switch(noKeysAssistanceStatus.value)
            {
                case 'loading':
                    return `flex items-center justify-center`;
                case 'success':
                    return `text-base text-light-green-500 font-semibold`;
                case 'void': default:
                    return 'text-sm text-gray-400';
            }
        });

        /**
         *
         * Methods
         *
         */
        const init = async () =>
        {
            if(store.state.auth.user)
            {
                if(route.query.cancel && store.state.stripe.sessionId)
                {
                    await expiresSpecificCheckoutSession({ session: store.state.stripe.sessionId});
                }
                // registration phase completed
                completionNumber.value = 1;
                if(store.state.auth.user?.supplier_reference_id)
                {
                    // Step 2 - subscription
                    completionNumber.value = 2;
                    // Check if user has an active subscription - if not, create new checkout
                    verifyUserSubscription();
                }
            }

        };
        const signout = () => 
        {   logoutStatus.value = 'loading';
            logout()
            .then( res => 
            {
                if(res === 200) return router.push(`/${store.state.locale}/signin`);
                else 
                {
                    logoutStatus.value = 'error';
                    setTimeout( () => logoutStatus.value = 'void', 2000);
                }
            });
        };
        /**
         * The supplier links itself to our SP account on the ZDHC Gateway
         * and an admin confirms the association on our side: this button
         * only refreshes the user to see whether that happened (plan E1/E2).
         */
        const checkSupplierLink = async () =>
        {
            linkCheckStatus.value = 'loading';
            const res = await checkAuthuser();
            if(res === 200 && store.state.auth.user?.supplier_reference_id)
            {
                completionNumber.value = 2;
                verifyUserSubscription();
            }
            else
            {
                addSnackbar('warning', t('waitingPage.link.not_confirmed', locale.value));
            }
            linkCheckStatus.value = 'void';
        };
        const closeHelpModal =() => 
        {
            showHelpModal.value = false
        };
        const noKeysAssistanceRequest = async () => 
        {
            noKeysAssistanceStatus.value = 'loading';
            const res = await sendZDHCKeysRequestEmail();
            if(res === 200) setTimeout( () => noKeysAssistanceStatus.value = 'success', 3000);
            else setTimeout( () => noKeysAssistanceStatus.value = 'void', 3000);
        };
        /**
         * Last step of the new flow: verify the live link on the Gateway
         * and make sure an InCheck subscription exists — creating it is
         * safe (idempotent on the backend, billed off-platform by ZDHC).
         */
        const finalizeZdhcSetup = async () =>
        {
            generatingTokenStatus.value = 'loading';
            locale.value = store.state.locale;
            try
            {
                let gate = await linkStatus();
                if(gate && gate.link_status === LINK_STATUS.LINKED_NO_SUBSCRIPTION)
                {
                    const created = await createSubscription();
                    if(created && !created.error)
                    {
                        gate = { link_status: LINK_STATUS.READY, subscription: created.current_subscription ?? null };
                    }
                }
                if(gate && gate.link_status === LINK_STATUS.READY)
                {
                    if(gate.subscription && gate.subscription.end_date)
                    {
                        updateSuscriptionExpiration({subscription_expires_at: gate.subscription.end_date});
                    }
                    completionNumber.value = 4;
                    return generatingTokenStatus.value = 'void';
                }
                if(gate && gate.link_status === LINK_STATUS.NOT_LINKED)
                {
                    // The supplier unlinked (or was never confirmed): reopen
                    // the linking step with instructions (plan E4).
                    completionNumber.value = 1;
                    addSnackbar('warning', t('waitingPage.link.not_confirmed', locale.value));
                    return generatingTokenStatus.value = 'void';
                }
                return generatingTokenStatus.value = 'error';
            }
            catch(e)
            {
                addSnackbar('error', t('snackbar.refresh', store.state.locale));
                return generatingTokenStatus.value = 'error';
            }
        }
        const verifyUserSubscription = async () => 
        {
            if(
                exceptions_email.includes(store.state.auth.user.email.toLowerCase())
                || store.state.auth.user.in_exception)
            {
                completionNumber.value = 3;
                finalizeZdhcSetup();
            }
            else
            {
                setTimeout( async () => {
                    const res = await verifySubscription({price: selectedPriceId.value});
                    if(res.data.data.active.length)
                    {
                        let activeSubscription = res.data.data.active[0];
                        store.commit('auth/SET_ACTIVE_SUBSCRIPTION', activeSubscription);

                        // If user has an active subscription, go to step 3
                        completionNumber.value = 3;
                        finalizeZdhcSetup();
                    }
                    else
                    {
                        createNewCheckout();
                    }
                }, 3000);
            }
        };
        const createNewCheckout = async () => 
        {
            const res = await createCheckout({price: selectedPriceId.value});
            if(res)
            { 
                stripeUrl.value = res.data.data.checkout.url;
                if(res.data?.data?.checkout?.id) store.commit('stripe/SET_SESSION_ID', res.data.data.checkout.id);
            }
        };
        /**
         *
         * Hooks
         *
         */
        locale.value = store.state.locale;
        init();


        return {
            /* Data */
            logoutStatus,
            completionNumber,
            linkCheckStatus,
            showHelpModal,
            noKeysAssistanceStatus,
            supportEmail,
            stripeSubscriptions,
            stripeUrl,
            generatingTokenStatus,
            /* Computed */
            setLogoutContent,
            setLogoutClasses,
            setLinkCheckButtonContent,
            setNoKeysAssistanceButtonContent,
            setNoKeysAssistanceButtonClasses,
            /* Methods */
            signout,
            checkSupplierLink,
            closeHelpModal,
            noKeysAssistanceRequest
        }
    }
}
</script>